/*  ========================================================================
    # Sections - Image Gallery
    ========================================================================  */
import {
  motion,
  MotionConfig,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from 'motion/react';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import ContentLightbox from '../../components/ContentLightbox';
import ImageSlider from '../../components/ImageSlider/ImageSlider';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';
import ScrollSlider from '../../components/ScrollSlider';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';

// Grid repeats every 140 columns
const getColumnDefinition = {
  0: index => `${index * 70 + 1} / 18 span`,
  1: index => `${index * 70 + 1 + 8} / 12 span`,
  2: index => `${index * 70 + 1 + 22} / 18 span`,
  3: index => `${index * 70 + 1 + 42} / 12 span`,
  4: index => `${index * 70 + 1 + 44} / 24 span`
};

const ImageGallery = ({ images, lightbox, lightboxCaption }) => {
  const reduced = useReducedMotion();
  const lightboxRef = useRef(null);
  const [startIndex, setStartIndex] = React.useState(0);
  const __ = useTranslation();

  // Get viewport width for the slider scroll behavior
  const { viewportWidth } = useBreakpoints();

  // Scroll-progress-related logic
  const scrollAreaRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: scrollAreaRef,
    offset: ['start end', 'end start']
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Define individual scroll translates for each variant
  const variant1Translate = useTransform(() => `${progress.get() * -22}% ${progress.get() * 6}%`);
  const variant2Translate = useTransform(() => `${progress.get() * -30}% ${progress.get() * -10}%`);
  const variant3Translate = useTransform(() => `${progress.get() * -25}% ${progress.get() * 10}%`);
  const variant4Translate = useTransform(() => `${progress.get() * -30}% ${progress.get() * 10}%`);
  const variant5Translate = useTransform(() => `${progress.get() * -20}% ${progress.get() * -5}%`);

  // Combine all five translate variants into one array to use later
  const translates = [
    variant1Translate,
    variant2Translate,
    variant3Translate,
    variant4Translate,
    variant5Translate
  ];

  // Make sure that a picture with keyboard focus is in the viewport
  const ensureImageWrapperVisible = useCallback(
    event =>
      event.target.scrollIntoView({
        behavior: reduced ? 'instant' : 'smooth',
        block: 'nearest',
        inline: 'center'
      }),
    [reduced]
  );

  return (
    <MotionConfig reducedMotion='user'>
      <div className='s-image-gallery__scrollarea' ref={scrollAreaRef}>
        <div className='s-image-gallery__grid'>
          {images.map((img, i) => {
            const variant = i % 5;
            const variantIndex = Math.floor(i / 5);
            const imageJsx = (
              <LazyImage
                alt={img.alt}
                figureClass='s-image-gallery__figure'
                imageClass='s-image-gallery__image'
                placeholderUrl={img.sizes.placeholder}
                defaultUrl={img.sizes.medium}
              >
                <LazySource srcSet={img.sizes['1536x1536']} mediaMin='1024px' />
                <LazySource srcSet={img.sizes.medium_large} mediaMin='768px' />
              </LazyImage>
            );
            return lightbox ? (
              <motion.button
                style={{
                  // Translate information is pulled per variant from the array defined earlier
                  translate: reduced ? false : translates[variant],
                  // The variant index will be used to calculate the correct positioning in the grid
                  gridColumn: getColumnDefinition[variant](variantIndex)
                }}
                className='s-image-gallery__image-wrapper s-image-gallery__image-wrapper--button'
                key={img.ID}
                data-variant={variant}
                tabIndex={0} // Make keyboard-focusable
                onFocus={ensureImageWrapperVisible}
                onClick={() => {
                  setStartIndex(i);
                  lightboxRef.current.open();
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                title={__('gallery-open-lightbox')}
                transition={{ visualDuration: 0.4, type: 'spring', bounce: 0.3 }}
              >
                {imageJsx}
              </motion.button>
            ) : (
              <motion.div
                style={{
                  // Translate information is pulled per variant from the array defined earlier
                  translate: reduced ? false : translates[variant],
                  // The variant index will be used to calculate the correct positioning in the grid
                  gridColumn: getColumnDefinition[variant](variantIndex)
                }}
                className='s-image-gallery__image-wrapper'
                key={img.ID}
                data-variant={variant}
                tabIndex={0} // Make keyboard-focusable
                onFocus={ensureImageWrapperVisible}
              >
                {imageJsx}
              </motion.div>
            );
          })}
        </div>
      </div>
      <div className='container'>
        <ScrollSlider scrollAreaRef={scrollAreaRef} scrollBy={viewportWidth / 2} />
      </div>
      <ContentLightbox lightboxRef={lightboxRef} type='fluid' size='fullscreen'>
        <ImageSlider
          startIndex={startIndex}
          images={images}
          infinite
          caption={lightboxCaption}
          fillParent
        />
      </ContentLightbox>
    </MotionConfig>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      ID: PropTypes.number,
      alt: PropTypes.string,
      sizes: PropTypes.object, // No more detail here
      url: PropTypes.string,
      srcSet: PropTypes.string
    })
  ),
  lightbox: PropTypes.bool,
  lightboxCaption: PropTypes.bool
};

export default ImageGallery;
